import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LuppApi } from '../lupp.service';
import { LUPP_QUERY_KEYS } from '../constants.lupp';
import {
  FleetHealthAlert,
  AlertSearchRequest,
  AlertListResponse,
  UseGetAlertsParams,
  UseGetAlertDetailsParams,
  ResolveAlertRequest,
  ResolveAlertResponse,
} from './dto.fhm.alerts';
import { zFleetHealthAlert, zAlertListResponse } from './validators';

/*
|--------------------------------------------------------------------------
| GET ALERTS (using POST /searches for server-side search/sort/pagination)
|--------------------------------------------------------------------------
*/

export const useGetAlerts = ({
  page = 0,
  size = 10,
  searchString,
  sort,
  enabled = true,
}: UseGetAlertsParams = {}) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.FHM_ALERTS, page, size, searchString, sort],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: String(page),
        size: String(size),
        sortDirection: 'DESC',
      });

      const body: AlertSearchRequest = {};
      if (searchString) body.searchString = searchString;
      if (sort) body.sort = sort;

      // Backend ApiResponsePayload structure: { data: { alerts: [], totalCount, ... }, message, ... }
      // LuppApi.extractData automatically extracts response.data, so we receive the inner object directly
      const response = await LuppApi.post<
        AlertSearchRequest,
        {
          alerts: FleetHealthAlert[];
          totalCount?: number;
          pageNumber?: number;
          pageSize?: number;
          currentPageCount?: number;
          totalPages?: number;
        }
      >(`service/v1/vehicles/fhm/alerts/searches?${queryParams.toString()}`, body);

      // Transform backend response to our AlertListResponse format
      const alertListResponse: AlertListResponse = {
        items: response?.alerts || [],
        totalCount: response?.totalCount || 0,
        pageNumber: response?.pageNumber || 0,
        pageSize: response?.pageSize || size,
        currentPageCount: response?.currentPageCount || response?.alerts?.length || 0,
        totalPages: response?.totalPages || 1,
      };

      const parsed = zAlertListResponse.safeParse(alertListResponse);
      if (!parsed.success) {
        console.error('Alert list response validation failed:', parsed.error);
        throw new Error('Invalid alert list response schema');
      }
      return parsed.data;
    },
    placeholderData: (previousData) => previousData,
    enabled,
  });
};

// Legacy alias for backwards compatibility (returns all alerts, page 0, size 100)
export const useAlerts = () => useGetAlerts({ page: 0, size: 100 });

/*
|--------------------------------------------------------------------------
| GET ALERT DETAILS
|--------------------------------------------------------------------------
*/

export const useGetAlertDetails = ({ alertId }: UseGetAlertDetailsParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.FHM_ALERT_DETAIL, alertId],
    queryFn: async () => {
      const alert = await LuppApi.get<FleetHealthAlert>(
        `service/v1/vehicles/fhm/alerts/${alertId}`
      );

      const parsed = zFleetHealthAlert.safeParse(alert);
      if (!parsed.success) {
        throw new Error('Invalid alert response schema');
      }
      return parsed.data;
    },
    enabled: !!alertId,
  });
};

// Legacy alias for backwards compatibility
export const useAlertDetails = (alertId: string) => useGetAlertDetails({ alertId });

/*
|--------------------------------------------------------------------------
| RESOLVE ALERT
|--------------------------------------------------------------------------
*/

export const useResolveAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ alertId, data }: { alertId: string; data: ResolveAlertRequest }) => {
      const response = await LuppApi.patch<ResolveAlertRequest, ResolveAlertResponse>(
        `service/v1/vehicles/fhm/alerts/${alertId}/resolve`,
        data
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch alerts list
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.FHM_ALERTS] });
    },
  });
};
